<template>
  <div
    ref="root"
    class="md:flex md:justify-center"
    :class="[
      {
        'md:items-stretch': stretch,
        'md:items-center': !stretch,
      },
    ]"
  >
    <div
      v-if="!withoutLeftTemplate"
      class="max-w-full md:relative md:mr-32 md:min-w-[337px] md:max-w-[498px] md:grow lg:mr-64"
    >
      <div
        class="flex w-full flex-col justify-center opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none"
        :class="[slotClasses.left]"
      >
        <slot name="left" />
      </div>
    </div>

    <div class="md:w-[360px] md:shrink-0 min-[900px]:w-[456px]">
      <div
        class="opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none"
        :class="[slotClasses.right]"
      >
        <slot name="right" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onUnmounted, ref } from 'vue'

import type { Grade } from '@backmarket/http-api'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useIntersectionObserver } from '@vueuse/core'

const { trackProductBlockImpression } = useTracking()
const root = ref<HTMLElement | null>()

const THRESHOLDS: Record<string, number> = {
  default: 0.5,
  recap: 0.25,
}

const props = withDefaults(
  defineProps<{
    slotClasses?: { left?: string; right?: string }
    tracking?: {
      trackingProducts?: Array<{
        product_id: string | undefined
        product_price: string | undefined | null
        product_variant: Grade['value'] | undefined | null
        product_special_offer: number | undefined | null
        product_available: boolean
        product_option: string | I18nDefinition<string>
      }>
      trackingTags?: {
        tagDisplayed: string
        tagSelected: boolean
      }
      trackingId?: string
      trackingModel?: string
      trackingCategory?: string
    }
    stretch?: boolean
    withoutLeftTemplate?: boolean
  }>(),
  {
    slotClasses: () => ({
      left: '',
      right: '',
    }),
    tracking: () => ({}),
    stretch: false,
    withoutLeftTemplate: false,
  },
)

function trackImpression() {
  if (props.tracking.trackingId) {
    trackProductBlockImpression({
      products: props.tracking.trackingProducts ?? [],
      product_price_tag_displayed:
        props.tracking.trackingTags?.tagDisplayed ?? 'no_tag',
      product_price_tag_selected: Boolean(
        props.tracking.trackingTags?.tagSelected,
      ),
      block_name: props.tracking.trackingId,
      product_model: props.tracking.trackingModel || '',
      product_category: props.tracking.trackingCategory || '',
    })
  }
}

const { stop } = useIntersectionObserver(
  root,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      trackImpression()
    }
  },
  {
    threshold:
      props.tracking.trackingId && props.tracking.trackingId in THRESHOLDS
        ? THRESHOLDS[props.tracking.trackingId]
        : THRESHOLDS.default,
  },
)

onUnmounted(() => {
  stop()
})
</script>
